/* DPAPage — /legal/dpa
 *
 * Data Processing Agreement template — intended to be downloaded and
 * signed by paying customers. Numbered like a contract so it can be
 * exported as PDF via browser print without losing structure.
 *
 * REVIEW BY COUNSEL BEFORE COUNTERSIGNING WITH ANY CUSTOMER.
 */
import LegalLayout from '../../components/legal/LegalLayout';
import { Button } from '../../components/ui/button';
import { Download } from 'lucide-react';

export default function DPAPage() {
  return (
    <LegalLayout title="Data Processing Agreement" lastUpdated="8 February 2026" activePath="/legal/dpa">
      <div className="-mt-4 mb-4 flex flex-wrap items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <p className="text-sm text-emerald-900 flex-1 m-0">
          This DPA forms part of your HyreDocs service agreement. To countersign, download the PDF and email a signed copy to <a href="mailto:contracts@hyredocs.com" className="font-semibold">contracts@hyredocs.com</a>.
        </p>
        <Button onClick={() => window.print()} variant="outline" className="border-emerald-700 text-emerald-700 hover:bg-emerald-100" data-testid="dpa-download">
          <Download className="w-4 h-4 mr-2" /> Print / Save as PDF
        </Button>
      </div>

      <h2>1. Parties</h2>
      <p>
        This Data Processing Agreement (&quot;<strong>DPA</strong>&quot;) is entered into between:
      </p>
      <ul>
        <li><strong>HyreDocs Ltd</strong>, a company registered in the United Kingdom (&quot;<strong>Processor</strong>&quot;); and</li>
        <li>The <strong>Customer</strong> identified in the underlying HyreDocs Service Agreement (&quot;<strong>Controller</strong>&quot;).</li>
      </ul>

      <h2>2. Definitions</h2>
      <p>Terms used but not defined here have the meanings given in the UK GDPR.</p>
      <ul>
        <li><strong>Personal Data</strong>: any information relating to an identified or identifiable natural person processed under the Service Agreement.</li>
        <li><strong>Sub-processor</strong>: any third party engaged by the Processor to process Personal Data on the Controller&apos;s behalf.</li>
        <li><strong>Service</strong>: the HyreDocs SaaS platform.</li>
      </ul>

      <h2>3. Subject matter and duration</h2>
      <p>
        The Processor processes Personal Data on behalf of the Controller solely for the purpose of providing the Service. Processing continues for the duration of the Service Agreement and any wind-down period agreed in writing.
      </p>

      <h2>4. Nature, purpose and categories of processing</h2>
      <ul>
        <li><strong>Nature:</strong> hosting, storage, transmission, signature collection, audit logging, integration with the Controller&apos;s connected ATS (Bullhorn).</li>
        <li><strong>Purpose:</strong> enabling the Controller to collect candidate information and execute employment / onboarding documents.</li>
        <li><strong>Data subjects:</strong> the Controller&apos;s candidates, contractors, clients and own employees.</li>
        <li><strong>Categories of Personal Data:</strong> contact details, identification data, employment data, signed documents, and the categories listed in clause 2 of the Privacy Policy.</li>
        <li><strong>Special categories:</strong> only where the Controller&apos;s template requires it (e.g. DBS certificates). The Controller is responsible for having a lawful basis for processing special-category data.</li>
      </ul>

      <h2>5. Obligations of the Processor</h2>
      <p>The Processor shall:</p>
      <ol>
        <li>process Personal Data only on documented instructions from the Controller, including with regard to transfers outside the UK / EEA;</li>
        <li>ensure persons authorised to process Personal Data are bound by a duty of confidentiality;</li>
        <li>take all technical and organisational measures required by Article 32 of the UK GDPR (see Schedule 1);</li>
        <li>engage Sub-processors only in accordance with clause 7 below;</li>
        <li>assist the Controller, by appropriate technical and organisational measures, in responding to data-subject requests (clause 8);</li>
        <li>assist the Controller in complying with its obligations under Articles 32 to 36 of the UK GDPR (security, breach notification, DPIA, prior consultation);</li>
        <li>at the Controller&apos;s choice, delete or return all Personal Data after the end of the provision of services, and delete existing copies unless UK law requires retention;</li>
        <li>make available all information necessary to demonstrate compliance with this DPA and allow for and contribute to audits, including inspections, conducted by the Controller or another auditor mandated by the Controller, on reasonable prior notice and subject to confidentiality.</li>
      </ol>

      <h2>6. Obligations of the Controller</h2>
      <p>The Controller warrants that it has a lawful basis for the processing it instructs the Processor to perform, that it has issued appropriate privacy notices to its data subjects, and that its instructions to the Processor will not cause the Processor to be in breach of any applicable data-protection law.</p>

      <h2>7. Sub-processors</h2>
      <p>
        The Controller grants the Processor a general written authorisation to engage Sub-processors. The current list is published at <a href="/legal/subprocessors">/legal/subprocessors</a> and forms part of this DPA. The Processor will notify the Controller of any intended changes at least 30 days in advance, giving the Controller the opportunity to object. If the Controller raises a reasonable objection on data-protection grounds and the parties cannot agree a resolution within 30 days, the Controller may terminate the Service Agreement on written notice, with a pro-rata refund of pre-paid fees.
      </p>
      <p>
        The Processor remains fully liable to the Controller for the performance of any Sub-processor&apos;s obligations.
      </p>

      <h2>8. Data-subject requests</h2>
      <p>
        Taking into account the nature of the processing, the Processor shall assist the Controller by appropriate technical and organisational measures, insofar as this is possible, in fulfilling the Controller&apos;s obligation to respond to requests for exercising the data subject&apos;s rights. The Processor will respond to documented requests from the Controller within 5 working days.
      </p>

      <h2>9. Personal data breach</h2>
      <p>
        The Processor shall notify the Controller without undue delay and in any event within 48 hours of becoming aware of a Personal Data Breach. The notification will include, to the extent known: the nature of the breach, categories and approximate number of data subjects and records affected, likely consequences, and measures taken or proposed to address the breach.
      </p>

      <h2>10. International transfers</h2>
      <p>
        Where the Processor transfers Personal Data outside the UK / EEA, the parties incorporate the UK International Data Transfer Addendum (Version B1.0, in force 21 March 2022) to the EU Standard Contractual Clauses (Module Two: Controller to Processor) by reference, with the Controller as data exporter and the Processor as data importer.
      </p>

      <h2>11. Liability</h2>
      <p>
        Each party&apos;s liability under this DPA is subject to the limitations of liability set out in the underlying Service Agreement.
      </p>

      <h2>12. Conflict</h2>
      <p>
        In the event of any conflict between this DPA and the Service Agreement, this DPA prevails on data-protection matters.
      </p>

      <h2>Schedule 1 — Technical and organisational measures</h2>
      <p>The Processor implements the following measures (non-exhaustive):</p>
      <ul>
        <li>TLS 1.2+ encryption in transit on every connection.</li>
        <li>Encryption at rest on the managed database tier (AES-256).</li>
        <li>Application-level encryption (Fernet / AES-128 GCM) for high-sensitivity secrets including OAuth tokens.</li>
        <li>Password hashing with <code>bcrypt</code> and per-password salts.</li>
        <li>HTTP-only secure cookies for session tokens; no Personal Data in localStorage.</li>
        <li>Strict per-tenant data isolation enforced at the API layer.</li>
        <li>Role-based access controls and audit logging of administrative actions.</li>
        <li>Least-privilege access for engineers; quarterly review.</li>
        <li>Automated dependency vulnerability scanning.</li>
        <li>Documented backup and disaster-recovery procedures with periodic restore drills.</li>
        <li>Breach-response runbook with 48-hour notification commitment to controllers.</li>
      </ul>

      <h2>Signatures</h2>
      <p>
        For HyreDocs Ltd: ___________________________  Date: _______________<br />
        For the Customer: ___________________________  Date: _______________
      </p>
    </LegalLayout>
  );
}
